import React from 'react';
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, MenuItem, Paper, Stack, Table,
  TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/global/Layout/MainLayout/NavigationBar';
import { useGetBillingCustomers, useGetBillingPeriods } from '../../hook/query/useBilling';
import { billingRelatedUrl } from './billingAdminFlow';
import { IBillingCustomer } from '../../compiler/billing.interface';
import { formatThaiCalendarDate } from '../../utilities/bangkokDateTime';

const money = (amount: number) => Number(amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const BillingInvoiceListPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const { data: periodsResponse } = useGetBillingPeriods();
  const periods = periodsResponse?.data?.periods ?? [];
  const start = query.get('start_date') || periods[0]?.start_date || '';
  const end = query.get('end_date') || periods[0]?.end_date || '';
  const customerCode = query.get('customer_code') || '';
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState<'all' | 'paid' | 'pending' | 'partial'>('all');
  const [page, setPage] = React.useState(0);
  const [limit, setLimit] = React.useState(50);
  const [selected, setSelected] = React.useState<string[]>([]);
  const { data, isLoading, isFetching, isError } = useGetBillingCustomers({ start_date: start, end_date: end,
    customer_code: customerCode || undefined, search: search || undefined,
    status: status === 'all' ? undefined : status, page: page + 1, limit });
  const customers = data?.data?.customers ?? [];
  const summary = data?.data?.summary;
  const total = data?.data?.pagination.total || 0;
  React.useEffect(() => { setPage(0); setSelected([]); }, [start, end, customerCode, search, status]);
  const viewCustomer = (customer: IBillingCustomer) => {
    const params = new URLSearchParams({ start_date: start, end_date: end, customerNo: customer.customer_code,
      customerName: customer.customer_name, round: `${formatThaiCalendarDate(start)} – ${formatThaiCalendarDate(end)}` });
    navigate(`/sales/billing-company-invoices?${params}`);
  };
  const toggle = (code: string) => setSelected(previous => previous.includes(code) ? previous.filter(item => item !== code) : [...previous, code]);
  const allOnPage = customers.length > 0 && customers.every(customer => selected.includes(customer.customer_code));
  const selectPage = () => setSelected(previous => allOnPage
    ? previous.filter(code => !customers.some(customer => customer.customer_code === code))
    : Array.from(new Set([...previous, ...customers.map(customer => customer.customer_code)])));
  return <>
    <NavigationBar title="ลูกค้าในรอบวางบิล" historyBack="function" />
    <Stack spacing={2} sx={{ py: 2 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <TextField select label="รอบ Invoice" value={`${start}|${end}`} size="small" sx={{ minWidth: 230 }}
          onChange={event => {
            const [nextStart, nextEnd] = event.target.value.split('|');
            const params = new URLSearchParams({ start_date: nextStart, end_date: nextEnd });
            if (customerCode) params.set('customer_code', customerCode);
            navigate(`/sales/billing-invoices?${params}`);
          }}>
          {!periods.some(period => period.start_date === start && period.end_date === end) &&
            <MenuItem value={`${start}|${end}`}>{start && end ? `${formatThaiCalendarDate(start)} – ${formatThaiCalendarDate(end)}` : 'รอรอบบิล'}</MenuItem>}
          {periods.map(period => <MenuItem key={`${period.start_date}|${period.end_date}`} value={`${period.start_date}|${period.end_date}`}>{period.period_label}</MenuItem>)}
        </TextField>
        <TextField label="ค้นหารหัส / ชื่อลูกค้า" size="small" value={search} onChange={event => setSearch(event.target.value)} />
        <TextField select label="สถานะชำระ" size="small" value={status} onChange={event => setStatus(event.target.value as typeof status)} sx={{ minWidth: 150 }}>
          <MenuItem value="all">ทั้งหมด</MenuItem><MenuItem value="pending">ยังไม่ชำระ</MenuItem>
          <MenuItem value="partial">ชำระบางส่วน</MenuItem><MenuItem value="paid">ชำระครบ</MenuItem>
        </TextField>
        {customerCode && <Chip label={`ลูกค้า ${customerCode}`} />}
        <Button variant="outlined" disabled={!selected.length || isFetching} onClick={() => navigate(billingRelatedUrl('email', start, end, selected))}>
          จัดการอีเมลลูกค้าที่เลือก ({selected.length})
        </Button>
      </Stack>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        <Typography>Invoice {Number(summary?.total_invoices || 0).toLocaleString()} ใบ</Typography>
        <Typography>ยอดรวม {money(summary?.total_amount || 0)}</Typography>
        <Typography>รับชำระแล้ว {money(summary?.paid_amount || 0)}</Typography>
        <Typography>คงเหลือ {money(summary?.outstanding_amount || 0)}</Typography>
      </Stack>
      {isError && <Alert severity="error">ไม่สามารถโหลดลูกค้าในรอบนี้ได้ กรุณาตรวจสิทธิ์และลองอีกครั้ง</Alert>}
      {isLoading ? <CircularProgress /> : <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small"><TableHead><TableRow>
          <TableCell padding="checkbox"><Checkbox checked={allOnPage} disabled={isFetching} onChange={selectPage} inputProps={{ 'aria-label': 'เลือกลูกค้าในหน้านี้' }} /></TableCell>
          <TableCell>ลูกค้า</TableCell><TableCell>Invoice / PDF</TableCell><TableCell align="right">ยอดรวม</TableCell>
          <TableCell align="right">รับชำระแล้ว</TableCell><TableCell align="right">คงเหลือ</TableCell><TableCell>สถานะ</TableCell><TableCell>ดำเนินการ</TableCell>
        </TableRow></TableHead><TableBody>
          {customers.map(customer => <TableRow key={customer.customer_code}>
            <TableCell padding="checkbox"><Checkbox checked={selected.includes(customer.customer_code)} disabled={isFetching} onChange={() => toggle(customer.customer_code)} inputProps={{ 'aria-label': `เลือก ${customer.customer_code}` }} /></TableCell>
            <TableCell>{customer.customer_code}<Typography variant="body2">{customer.customer_name}</Typography><Typography variant="caption">{customer.customer_email || '-'}</Typography></TableCell>
            <TableCell>{customer.invoice_count} / {customer.billing_file_count}</TableCell>
            <TableCell align="right">{money(customer.total_amount)}</TableCell><TableCell align="right">{money(customer.paid_amount)}</TableCell>
            <TableCell align="right">{money(customer.outstanding_amount)}</TableCell>
            <TableCell><Chip size="small" label={customer.payment_status === 'paid' ? 'ชำระครบ' : customer.payment_status === 'partial' ? 'ชำระบางส่วน' : 'ยังไม่ชำระ'} />
              <Typography variant="caption" display="block">{customer.email_sent ? 'มีประวัติส่งอีเมล' : 'ยังไม่ส่งอีเมล'}</Typography></TableCell>
            <TableCell><Stack direction="row" spacing={1}>
              <Button onClick={() => viewCustomer(customer)}>ดูบิล / รับชำระ</Button>
              <Button disabled={!customer.customer_folder_url} onClick={() => customer.customer_folder_url && window.open(customer.customer_folder_url, '_blank', 'noopener,noreferrer')}>เอกสาร</Button>
              <Button onClick={() => navigate(billingRelatedUrl('email', start, end, [customer.customer_code]))}>อีเมล</Button>
            </Stack></TableCell>
          </TableRow>)}
          {!customers.length && <TableRow><TableCell colSpan={8}>ไม่พบลูกค้าตามเงื่อนไข</TableCell></TableRow>}
        </TableBody></Table>
        <TablePagination component="div" count={total} page={page} rowsPerPage={limit} rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(_, next) => setPage(next)} onRowsPerPageChange={event => { setLimit(Number(event.target.value)); setPage(0); }} labelRowsPerPage="แสดง" />
      </Paper>}
    </Stack>
  </>;
};
export default BillingInvoiceListPage;
